// src/pages/VerifyEmail.jsx
import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const emailFromQuery = searchParams.get("email") || "";
  const [email, setEmail] = useState(emailFromQuery);
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // 初始时，从 localStorage 里补全 email
  useEffect(() => {
    if (!emailFromQuery) {
      const saved = localStorage.getItem("pendingEmail");
      if (saved) {
        setEmail(saved);
      }
    }
  }, [emailFromQuery]);

  // 一个小工具：拿到「真正要传给 Cognito 的 username」
  const getUsernameForCognito = () => {
    // 我们在注册时把 username 存成 pendingUsername
    const u = localStorage.getItem("pendingUsername");
    if (u && u.trim()) return u.trim();

    // 兜底：尝试用 email（对老数据或 alias 场景可能也能用）
    return (email || "").trim();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setMsg("");
    setLoading(true);

    try {
      const username = getUsernameForCognito();
      if (!username) {
        setMsg("Missing username or email. Please try registering again.");
        return;
      }

      if (!code.trim()) {
        setMsg("Please enter the verification code.");
        return;
      }

      await Auth.confirmSignUp(username, code.trim());
      setMsg("Verification successful! You can now login.");

      // 验证成功后，可以清掉 pending 信息
      localStorage.removeItem("pendingUsername");
      localStorage.removeItem("pendingEmail");

      // 2 秒后跳回登录 / 注册页
      setTimeout(() => {
        navigate("/register");
      }, 2000);
    } catch (err) {
      console.error("Confirm error:", err);
      if (err.code === "CodeMismatchException") {
        setMsg("Invalid verification code provided, please try again.");
      } else if (err.code === "ExpiredCodeException") {
        setMsg("This code has expired. Please click 'Resend code' to get a new one.");
      } else {
        setMsg(err.message || "Verification failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setMsg("");
    setLoading(true);
    try {
      const username = getUsernameForCognito();
      if (!username) {
        setMsg("Missing username or email. Please try registering again.");
        return;
      }

      await Auth.resendSignUp(username);
      setMsg("A new verification code has been sent. Please use the latest email.");
    } catch (err) {
      console.error("Resend error:", err);
      setMsg(err.message || "Failed to resend verification code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-white px-4 font-sans tracking-wide">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl shadow-slate-200/70 p-8 border border-slate-100">
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-extrabold text-blue-600 mb-2 tracking-tight">
            Verify your email
          </h1>
          <p className="text-gray-600 text-sm">
            We have sent a 6-digit verification code to your email.
          </p>
        </div>

        {msg && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-300 text-amber-800 rounded-xl text-sm whitespace-pre-wrap">
            {msg}
          </div>
        )}

        <form onSubmit={handleVerify} className="space-y-4">
          <input
            type="email"
            value={email}
            readOnly
            className="w-full px-4 py-2 border border-slate-300 bg-slate-50 rounded-full text-sm"
          />

          <input
            type="text"
            placeholder="Enter 6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-full text-center tracking-[0.3em] text-lg"
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2.5 rounded-full text-sm font-semibold hover:bg-blue-700 transition shadow-md shadow-blue-200 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Verifying..." : "Verify"}
          </button>
        </form>

        <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
          <span>Didn&apos;t get the code?</span>
          <button
            type="button"
            onClick={handleResend}
            className="text-blue-600 hover:underline"
          >
            Resend code
          </button>
        </div>

        <div className="mt-4 text-center">
          <button
            type="button"
            className="text-xs text-gray-400 hover:text-gray-600"
            onClick={() => navigate("/register")}
          >
            Back to login / register
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
